import { useEffect, useState } from "react";
import axios from "axios";
import { Check, CheckCircle2, Download, Mail, Phone, ShieldCheck, X } from "lucide-react";
import { API } from "../lib/apiClient";

const BASE = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u && u.startsWith("/") ? `${BASE}${u}` : u);
const tl = (n) => (n ? `₺${Number(n).toLocaleString("tr-TR", { maximumFractionDigits: 0 })}` : "—");
const fmt = (s) => (s ? new Date(`${s.slice(0, 10)}T00:00:00`).toLocaleDateString("tr-TR") : "—");

function Logo({ q, size = 34 }) {
  const [ok, setOk] = useState(true);
  return q.logo && ok ? <img className="cs-logo" style={{ width: size, height: size }} src={abs(q.logo)} alt="" onError={() => setOk(false)} />
    : <span className="cs-logo cs-initial" style={{ width: size, height: size }}>{(q.company || "?")[0]}</span>;
}

function CompareTable({ items, bestId, selected, setSelected, done }) {
  const hasNet = items.some((i) => i.netPremium);
  const hasCov = items.some((i) => (i.coverages || "").trim());
  return (
    <div className="cs-scroll">
      <table className="cs-table" data-testid="choice-table">
        <thead><tr><th />{items.map((q) => (
          <th key={q.id} className={q.id === selected ? "sel" : ""}><Logo q={q} /><strong>{q.company || "Şirket"}</strong>{q.id === bestId && <em>EN UYGUN</em>}</th>
        ))}</tr></thead>
        <tbody>
          <tr><td>Brüt prim</td>{items.map((q) => <td key={q.id} className={`cs-big${q.id === selected ? " sel" : ""}`} data-testid={`choice-price-${q.id}`}>{tl(q.premium)}</td>)}</tr>
          {hasNet && <tr><td>Net prim</td>{items.map((q) => <td key={q.id} className={q.id === selected ? "sel" : ""}>{tl(q.netPremium)}</td>)}</tr>}
          {hasCov && <tr><td>Teminatlar</td>{items.map((q) => <td key={q.id} className={`cs-cov${q.id === selected ? " sel" : ""}`} data-testid={`choice-coverages-${q.id}`}>{q.coverages || "—"}</td>)}</tr>}
          <tr><td />{items.map((q) => (
            <td key={q.id} className={q.id === selected ? "sel" : ""}>
              <button type="button" className={`cs-pick${q.id === selected ? " on" : ""}`} disabled={done} onClick={() => setSelected(q.id)} data-testid={`choice-select-${q.id}`}>
                {q.id === selected ? <><Check size={14} /> Seçildi</> : "Bu Teklifi Seç"}
              </button>
            </td>
          ))}</tr>
        </tbody>
      </table>
    </div>
  );
}

export default function CompareChoicePage({ token }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState("");
  useEffect(() => {
    axios.get(`${API}/public/compare/${token}`).then(({ data: d }) => {
      setData(d); setNote(d.choice?.note || ""); setSelected(d.choice?.quoteId || d.bestId || d.quotes[0]?.id);
      if (d.rejected) setState("rejected");
    }).catch((e) => setErr(e?.response?.data?.detail || "Bağlantı açılamadı"));
  }, [token]);
  const confirm = async () => {
    setBusy(true);
    try { const { data: r } = await axios.post(`${API}/public/compare/${token}/choose`, { quoteId: selected, note }); setData((d) => ({ ...d, choice: r.choice })); setState("confirmed"); }
    catch (e) { window.alert(e?.response?.data?.detail || "Seçim kaydedilemedi"); } finally { setBusy(false); }
  };
  const reject = async () => {
    if (!window.confirm("Teklifleri reddetmek istediğinize emin misiniz?")) return;
    setBusy(true);
    try { await axios.post(`${API}/public/compare/${token}/reject`); setState("rejected"); } catch (e) { window.alert(e?.response?.data?.detail || "İşlem başarısız"); } finally { setBusy(false); }
  };
  if (err) return <div className="cs-page"><div className="cs-center" data-testid="choice-error">{err}</div></div>;
  if (!data) return <div className="cs-page"><div className="cs-center">Teklifler yükleniyor…</div></div>;
  const { agent } = data;
  const sel = data.quotes.find((q) => q.id === selected) || data.quotes[0];
  return (
    <div className="cs-page notranslate" translate="no" data-testid="compare-choice-page">
      <div className="cs-wrap">
        <header className="cs-hero">
          <div className="cs-agent">{agent.logoUrl ? <img src={abs(agent.logoUrl)} alt="" data-testid="choice-agent-logo" /> : <ShieldCheck size={18} />}<span>{agent.name || "Sigorta Danışmanınız"}</span></div>
          <h1>{data.branch ? `${data.branch} Sigortası Teklifi` : "Sigorta Teklifi"}</h1>
          <p>Sayın {data.customerName}, size özel hazırlanmıştır.</p>
          <div className="cs-hero-price"><small>BRÜT PRİM</small><b data-testid="choice-hero-price">{tl(sel?.premium)}</b><span>{sel?.company}</span></div>
        </header>
        {data.quotes.length > 1 && <h2 className="cs-h2">{data.quotes.length} ŞİRKET KARŞILAŞTIRMASI · BİRİNİ SEÇİN</h2>}
        <CompareTable items={data.quotes} bestId={data.bestId} selected={selected} setSelected={setSelected} done={!!state} />
        <section className="cs-card" data-testid="choice-detail">
          {[["Sigorta Şirketi", sel?.company], ["Teklif No", sel?.quoteNo || "—"], ["Net Prim", tl(sel?.netPremium)], ["Teklif Tarihi", fmt(data.createdAt)], ["Geçerlilik", fmt(sel?.expiryDate)]].map(([l, v]) => (
            <div key={l}><span>{l}</span><b>{v}</b></div>
          ))}
        </section>
        {state === "confirmed" ? (
          <div className="cs-done ok" data-testid="choice-confirmed"><CheckCircle2 size={22} /><div><strong>Seçiminiz iletildi</strong><span>{sel?.company} · {tl(sel?.premium)} — danışmanınız en kısa sürede sizinle iletişime geçecek.</span></div></div>
        ) : state === "rejected" ? (
          <div className="cs-done no" data-testid="choice-rejected"><X size={22} /><div><strong>Teklif reddedildi</strong><span>Fikriniz değişirse danışmanınızla iletişime geçebilirsiniz.</span></div></div>
        ) : (<>
          <label className="cs-note"><span>Danışmanınıza not (isteğe bağlı)</span>
            <textarea rows={2} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Örn. Akşam 18:00'den sonra arayabilirsiniz" data-testid="choice-note-input" /></label>
          <div className="cs-bar" data-testid="choice-bar">
            <div><small>Seçiminiz</small><strong>{sel?.company}</strong><b>{tl(sel?.premium)}</b></div>
            <div className="cs-bar-actions">
              <button type="button" className="cs-reject" disabled={busy} onClick={reject} data-testid="choice-reject"><X size={15} /> Reddet</button>
              <button type="button" className="cs-confirm" disabled={busy || !selected} onClick={confirm} data-testid="choice-confirm"><Check size={15} /> Seçimi Onayla</button>
            </div>
          </div>
        </>)}
        {data.pdfPath && <a className="cs-pdf" href={`${BASE}${data.pdfPath}`} target="_blank" rel="noreferrer" data-testid="choice-pdf-link"><Download size={15} /> PDF olarak indir</a>}
        <footer className="cs-foot">
          <small>TEMSİLCİNİZ</small>
          <strong>{agent.name}</strong>
          <div>{agent.phone && <a href={`tel:${agent.phone}`} data-testid="choice-call"><Phone size={13} /> {agent.phone}</a>}{data.agentEmail && <a href={`mailto:${data.agentEmail}`}><Mail size={13} /> {data.agentEmail}</a>}</div>
        </footer>
      </div>
    </div>
  );
}
